import Cookies from 'js-cookie'
import { useCallback, useContext } from 'react'

import { AttributeInput } from '@data/shopify/storefront/types'
import { PartnerAdsContext } from './partner-ads-context'

interface PartnerAdsVariables {
  partnerId?: string
  clickId?: string
}

const partnerIdParameter1 = 'paid'
const partnerIdParameter2 = 'pa-partnerid'
const clickIdParameter1 = 'pacid'
const clickIdParameter2 = 'pa-pacid'

const startTimeKey = 'x-pa-startTime'
const partnerIdKey = 'x-pa-id'
const clickIdKey = 'x-pa-pacid'

const partnerIdAttribute = 'x-pa-id'
const clickIdAttribute = 'x-pa-pacid'

const storageExpiryInDays = 40
const storageExpiryInMilliseconds = 1000 * 60 * 60 * 24 * storageExpiryInDays

/**
 * Reads PartnerAds parameter values from URL and stores them.
 */
export const loadPartnerAdsUrlParameters = () => {
  const url = new URL(window.location.href)

  if (
    (!url.searchParams.has(partnerIdParameter1) &&
      !url.searchParams.has(partnerIdParameter2)) ||
    (!url.searchParams.has(clickIdParameter1) &&
      !url.searchParams.has(clickIdParameter2))
  ) {
    return
  }

  const date = new Date().getTime().toString()
  const partnerId =
    url.searchParams.get(partnerIdParameter1) ??
    url.searchParams.get(partnerIdParameter2) ??
    ''
  const clickId =
    url.searchParams.get(clickIdParameter1) ??
    url.searchParams.get(clickIdParameter2) ??
    ''

  localStorage.setItem(startTimeKey, date)
  localStorage.setItem(partnerIdKey, partnerId)
  localStorage.setItem(clickIdKey, clickId)

  Cookies.set(startTimeKey, date, {
    expires: storageExpiryInDays,
  })
  Cookies.set(partnerIdKey, partnerId, {
    expires: storageExpiryInDays,
  })
  Cookies.set(clickIdKey, clickId, {
    expires: storageExpiryInDays,
  })
}

/**
 * Checks if the local storage item or cookie has expired.
 */
const getIsPartnerAdsExpired = () => {
  const date = new Date().getTime()
  const startTime =
    localStorage.getItem(startTimeKey) ?? Cookies.get(startTimeKey)

  return !startTime || date - Number(startTime) > storageExpiryInMilliseconds
}

/**
 * Gets stored PartnerAds parameters.
 */
const getPartnerAdsParameters = () => {
  const isPartnerAdsExpired = getIsPartnerAdsExpired()

  if (isPartnerAdsExpired) {
    localStorage.removeItem(partnerIdKey)
    localStorage.removeItem(clickIdKey)

    Cookies.remove(partnerIdKey)
    Cookies.remove(clickIdKey)

    return
  }

  const partnerAdsVariables: PartnerAdsVariables = {
    partnerId: localStorage.getItem(partnerIdKey) ?? Cookies.get(partnerIdKey),
    clickId: localStorage.getItem(clickIdKey) ?? Cookies.get(clickIdKey),
  }

  return partnerAdsVariables
}

/**
 * Returns a hook that gets attributes with PartnerAds data.
 */
export const useGetPartnerAdsAttributes = () => {
  const { partnerAdsEnabled } = useContext(PartnerAdsContext)

  return useCallback(
    (privateAttributes: boolean) => {
      if (!partnerAdsEnabled) {
        return []
      }

      const parameters = getPartnerAdsParameters()
      const partnerId = parameters?.partnerId
      const clickId = parameters?.clickId

      if (!partnerId || !clickId) {
        return []
      }

      const keyPrefix = privateAttributes ? '_' : ''
      const attributes: AttributeInput[] = [
        {
          key: `${keyPrefix}${partnerIdAttribute}`,
          value: partnerId,
        },
        {
          key: `${keyPrefix}${clickIdAttribute}`,
          value: clickId,
        },
      ]

      return attributes
    },
    [partnerAdsEnabled]
  )
}
